import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, RefreshCcw, AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

const fmt = (n: number | null | undefined) =>
  n == null ? "—" : n >= 999_000 ? "Ilimitado" : n.toLocaleString("pt-BR");

function ProgressBar({ value, max }: { value: number; max: number | null }) {
  if (!max || max <= 0 || max >= 999_000) {
    return <div className="text-[10px] text-muted-foreground">Ilimitado</div>;
  }
  const pct = Math.min(Math.round((value / max) * 100), 100);
  const color =
    pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="space-y-1">
      <div className="text-[11px] text-muted-foreground">
        {value.toLocaleString("pt-BR")} / {max.toLocaleString("pt-BR")} ({pct}%)
      </div>
      <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function UsageTab() {
  const { data, isLoading, refetch } = trpc.superAdmin.usageReport.useQuery();
  const resetMut = trpc.superAdmin.resetMonthlyCounters.useMutation({
    onSuccess: () => { toast.success("Contadores mensais resetados"); refetch(); },
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao resetar contadores")),
  });

  const [search, setSearch] = useState("");
  const [onlyAlerts, setOnlyAlerts] = useState(false);

  const rows = useMemo(() => {
    let r = data ?? [];
    if (search) {
      const s = search.toLowerCase();
      r = r.filter(x => x.name.toLowerCase().includes(s));
    }
    if (onlyAlerts) r = r.filter(x => x.alerts.length > 0);
    return r;
  }, [data, search, onlyAlerts]);

  const overall = useMemo(() => {
    if (!data) return { withAlerts: 0, overContacts: 0, overMsgs: 0 };
    return {
      withAlerts: data.filter(r => r.alerts.length > 0).length,
      overContacts: data.filter(r => r.alerts.includes("over_contacts_limit")).length,
      overMsgs: data.filter(r => r.alerts.includes("over_msgs_limit")).length,
    };
  }, [data]);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Uso & Quotas</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Consumo real por empresa, comparado ao limite do plano.
          </p>
        </div>
        <Button
          size="sm" variant="outline"
          onClick={() => {
            if (confirm("Zerar messagesThisMonth de todos os tenants? Use no início de cada mês."))
              resetMut.mutate();
          }}
          disabled={resetMut.isPending}
        >
          {resetMut.isPending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-1.5" />}
          Resetar contadores mensais
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Empresas com alerta</p>
          <p className="text-2xl font-bold mt-1 text-amber-600">{overall.withAlerts}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Estouraram contatos</p>
          <p className="text-2xl font-bold mt-1 text-red-600">{overall.overContacts}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Estouraram mensagens</p>
          <p className="text-2xl font-bold mt-1 text-red-600">{overall.overMsgs}</p>
        </CardContent></Card>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Input placeholder="Buscar empresa…" value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs" />
        <Button size="sm" variant={onlyAlerts ? "default" : "outline"} onClick={() => setOnlyAlerts(v => !v)}>
          <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
          {onlyAlerts ? "Mostrando só com alertas" : "Filtrar com alertas"}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Nenhuma empresa encontrada.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Plano</TableHead>
                  <TableHead className="min-w-[180px]">Contatos</TableHead>
                  <TableHead className="min-w-[180px]">Mensagens (mês)</TableHead>
                  <TableHead className="min-w-[140px]">Fluxos</TableHead>
                  <TableHead className="text-right">E-mails (mês)</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.tenantId}>
                    <TableCell>
                      <div className="font-medium text-sm">{r.name}</div>
                      {r.alerts.length > 0 && (
                        <div className="flex gap-1 flex-wrap mt-1">
                          {r.alerts.map(a => (
                            <Badge key={a} variant={a.startsWith("over_") ? "destructive" : "secondary"} className="text-[9px] h-4">
                              {a.replace("near_", "≈").replace("over_", "↑").replace("_limit", "").replace("_", " ")}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{r.plan}</Badge></TableCell>
                    <TableCell><ProgressBar value={r.contacts} max={r.contactsLimit} /></TableCell>
                    <TableCell><ProgressBar value={r.msgs} max={r.msgsLimit} /></TableCell>
                    <TableCell><ProgressBar value={r.flows} max={r.flowsLimit} /></TableCell>
                    <TableCell className="text-right font-medium text-sm">{fmt(r.emails)}</TableCell>
                    <TableCell>
                      <Badge variant={r.status === "active" ? "default" : r.status === "suspended" ? "destructive" : "secondary"} className="text-[10px]">
                        {r.status === "active" ? <><Check className="w-3 h-3 mr-1" />ativo</> : r.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
